/**
 * The colours an account with no picture is drawn in.
 *
 * A child's managed account never has a photo, so on the picker that asks
 * which of them is being entered the colour is what tells two siblings apart
 * at a glance — and it is the parent's to choose (`users.avatar_color`, see
 * AddAvatarColorToUsers). The names here are the server's closed set: it
 * refuses anything else, so the two lists have to stay in step.
 *
 * Full class strings in a map rather than `bg-${color}-100`, because Tailwind
 * only ships the classes it can see written down.
 */
export const AVATAR_COLORS = [
  'red',
  'orange',
  'amber',
  'lime',
  'emerald',
  'teal',
  'sky',
  'indigo',
  'violet',
  'fuchsia',
  'rose',
  'slate',
] as const;

export type AvatarColor = (typeof AVATAR_COLORS)[number];

/** The avatar itself: a wash with text dark enough to read on it, both themes. */
const FACES: Record<AvatarColor, string> = {
  red: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-50',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-50',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-50',
  lime: 'bg-lime-100 text-lime-800 dark:bg-lime-900/60 dark:text-lime-50',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-50',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/60 dark:text-teal-50',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-50',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-50',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-50',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/60 dark:text-fuchsia-50',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-50',
  slate: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-50',
};

/** The swatch on the palette: the colour at full strength, so it reads as a choice. */
const SWATCHES: Record<AvatarColor, string> = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  lime: 'bg-lime-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  sky: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  fuchsia: 'bg-fuchsia-500',
  rose: 'bg-rose-500',
  slate: 'bg-slate-500',
};

export function isAvatarColor(value: unknown): value is AvatarColor {
  return typeof value === 'string' && (AVATAR_COLORS as readonly string[]).includes(value);
}

/**
 * The colour a name gets when nobody has chosen one: stable for the same
 * person, so a ward created before this existed still looks like something and
 * does not change hue on every render.
 */
export function avatarColorFor(seed: string): AvatarColor {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 100000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function avatarFaceClass(color: string | null | undefined, seed = ''): string {
  return FACES[isAvatarColor(color) ? color : avatarColorFor(seed)];
}

export function avatarSwatchClass(color: AvatarColor): string {
  return SWATCHES[color];
}

/** Up to two letters, as an avatar with no picture shows a person. */
export function initialsOf(name?: string | null, surname?: string | null): string {
  const letters = [name, surname]
    .map((part) => part?.trim()?.[0] ?? '')
    .filter(Boolean)
    .join('');
  return (letters || '?').toUpperCase();
}
